/* eslint-disable @typescript-eslint/no-explicit-any */

export interface MfTextToggleOptions {
  /** CSS-Selector relativ zum Container für das Textelement */
  textSelector?: string;                       // default: ".project-info-text"
  /** Anzahl Zeilen im eingeklappten Zustand */
  collapsedLines?: number;                     // default: 4
  /** CSS-Klasse, die am Container beim Ausklappen gesetzt wird */
  expandedClass?: string;                      // default: "expanded"
  /** CSS-Klasse für den Toggle-Button */
  buttonClass?: string;                        // default: "text-toggle"
  /** Inhalt/Icon für "mehr" (HTML-String oder HTMLElement) */
  expandIcon?: string | HTMLElement;           // default: aus Template oder Text
  /** Inhalt/Icon für "weniger" (HTML-String oder HTMLElement) */
  collapseIcon?: string | HTMLElement;         // default: aus Template oder Text
  /** Button nur anzeigen, wenn wirklich gekürzt wird */
  showOnlyIfClamped?: boolean;                 // default: true
  /** ARIA-Labels */
  toggleLabel?: { expand: string; collapse: string }; // default: de
}

export default class mfTextToggle {
  private container: HTMLElement;
  private textEl: HTMLElement | null;
  private btn: HTMLButtonElement | null = null;
  private isExpanded = false;
  private opts: Required<MfTextToggleOptions>;
  private contentId!: string;

  constructor(container: HTMLElement, options: MfTextToggleOptions = {}) {
    this.container = container;

    // Defaults
    this.opts = {
      textSelector: options.textSelector ?? ".project-info-text",
      collapsedLines: options.collapsedLines ?? 4,
      expandedClass: options.expandedClass ?? "expanded",
      buttonClass: options.buttonClass ?? "text-toggle",
      expandIcon: options.expandIcon ?? getIconFromContainerOrGlobal(container, "expand"),
      collapseIcon: options.collapseIcon ?? getIconFromContainerOrGlobal(container, "collapse"),
      showOnlyIfClamped: options.showOnlyIfClamped ?? true,
      toggleLabel: options.toggleLabel ?? {
        expand: "Mehr anzeigen",
        collapse: "Weniger anzeigen",
      },
    };

    this.textEl = this.container.querySelector<HTMLElement>(this.opts.textSelector) ?? null;
    if (!this.textEl) {
      console.warn(`mfTextToggle: Kein Textelement für "${this.opts.textSelector}" in`, this.container);
      return;
    }

    // ID für aria-controls
    this.contentId = this.textEl.id || `mf-tt-${Math.random().toString(36).slice(2)}`;
    if (!this.textEl.id) this.textEl.id = this.contentId;

    // Startzustand: gekürzt
    this.applyClamp(this.opts.collapsedLines);

    // Button bauen und direkt nach dem Textelement einfügen
    this.btn = this.buildButton();
    this.textEl.insertAdjacentElement("afterend", this.btn);

    // Button nur zeigen, wenn es auch wirklich clamped ist
    if (this.opts.showOnlyIfClamped) {
      requestAnimationFrame((): void => {
        if (!this.textEl || !this.btn) return;
        const isClamped = this.isActuallyClamped(this.textEl);
        this.btn.style.display = isClamped ? "" : "none";
      });
    }
  }

  /** Öffentliche API */
  public expand(): void {
    if (!this.textEl || !this.btn) return;
    this.isExpanded = true;
    this.container.classList.add(this.opts.expandedClass);
    this.removeClamp();
    this.updateButton();
  }

  public collapse(): void {
    if (!this.textEl || !this.btn) return;
    this.isExpanded = false;
    this.container.classList.remove(this.opts.expandedClass);
    this.applyClamp(this.opts.collapsedLines);
    this.updateButton();
  }

  public toggle(): void {
    this.isExpanded ? this.collapse() : this.expand();
  }

  /** Button, Styles & State vollständig zurücksetzen */
  public destroy(): void {
    // Button entfernen
    if (this.btn) {
      this.btn.removeEventListener("click", this.onClick);
      this.btn.remove();
      this.btn = null;
    }

    // Container zurücksetzen
    this.container.classList.remove(this.opts.expandedClass);

    // Inline-Styles vom Text-Element löschen
    if (this.textEl) {
      const s = this.textEl.style;
      s.removeProperty("-webkit-line-clamp");
      s.removeProperty("-webkit-box-orient");
      s.removeProperty("overflow");
      s.removeProperty("display");
      s.removeProperty("transition");
    }

    // internen Zustand zurücksetzen
    this.isExpanded = false;
  }

  /** --- interne Helfer --- */

  private onClick = (ev: MouseEvent): void => {
    ev.preventDefault();
    this.toggle();
  };

  private buildButton(): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = this.opts.buttonClass;
    btn.setAttribute("aria-controls", this.contentId);
    btn.setAttribute("aria-expanded", "false");
    btn.addEventListener("click", this.onClick);
    this.setButtonContent(btn, this.opts.expandIcon, this.opts.toggleLabel.expand);
    return btn;
  }

  private updateButton(): void {
    if (!this.btn) return;
    this.btn.setAttribute("aria-expanded", this.isExpanded ? "true" : "false");
    if (this.isExpanded) {
      this.setButtonContent(this.btn, this.opts.collapseIcon, this.opts.toggleLabel.collapse);
    } else {
      this.setButtonContent(this.btn, this.opts.expandIcon, this.opts.toggleLabel.expand);
    }
  }

  private setButtonContent(btn: HTMLButtonElement, content: string | HTMLElement, fallbackLabel: string): void {
    btn.innerHTML = "";
    if (content instanceof HTMLElement) {
      // Template-Content ggf. klonen
      const node = content.tagName.toLowerCase() === "template"
        ? (content as HTMLTemplateElement).content.cloneNode(true)
        : content.cloneNode(true);
      btn.appendChild(node);
      if (!btn.getAttribute("aria-label")) btn.setAttribute("aria-label", fallbackLabel);
    } else {
      btn.innerHTML = content;
      btn.setAttribute("aria-label", fallbackLabel);
    }
  }

  /** line-clamp via Inline-Styles */
  private applyClamp(lines: number): void {
    if (!this.textEl) return;
    const s = this.textEl.style;
    s.display = "-webkit-box";
    s.setProperty("-webkit-box-orient", "vertical");
    s.setProperty("-webkit-line-clamp", String(lines));
    s.overflow = "hidden";
    if (!s.transition) s.transition = "all 0.3s ease";
  }

  private removeClamp(): void {
    if (!this.textEl) return;
    const s = this.textEl.style;
    s.removeProperty("-webkit-line-clamp");
    s.removeProperty("-webkit-box-orient");
    s.removeProperty("overflow");
    s.removeProperty("display");
  }

  private isActuallyClamped(el: HTMLElement): boolean {
    const epsilon = 1;
    return el.scrollHeight - el.clientHeight > epsilon;
  }

  /** Alle passenden Container initialisieren */
  public static initAll(selector = ".info-col", options: MfTextToggleOptions = {}): mfTextToggle[] {
    const instances: mfTextToggle[] = [];
    document.querySelectorAll<HTMLElement>(selector).forEach((wrap) => {
      instances.push(new mfTextToggle(wrap, options));
    });
    return instances;
  }
}

/* ------------------------ Icon-Lookup Utilities ------------------------ */

/**
 * Holt das Icon aus (in dieser Reihenfolge):
 * 1) data-Attribut am Container: data-expand-icon / data-collapse-icon  (CSS-Selector)
 * 2) globales Template: #mf-expand-icon / #mf-collapse-icon
 * 3) Fallback: Text "Mehr/Weniger"
 */
function getIconFromContainerOrGlobal(container: HTMLElement, kind: "expand" | "collapse"): string | HTMLElement {
  // 1) per-container data attribute (Selector auf ein <template> oder ein Element)
  const dataAttr = kind === "expand" ? "expandIcon" : "collapseIcon";
  const sel = container.dataset[dataAttr as keyof DOMStringMap];
  if (sel) {
    const el = document.querySelector<HTMLElement>(sel);
    if (el) return el;
  }

  // 2) globale Templates
  const globalId = kind === "expand" ? "#mf-expand-icon" : "#mf-collapse-icon";
  const tpl = document.querySelector<HTMLElement>(globalId);
  if (tpl) return tpl;

  // 3) Fallback-Text
  return kind === "expand" ? "Mehr" : "Weniger";
}

/* ------------------------ Responsive Lifecycle ------------------------ */
/**
 * Auto-Bootstrap OHNE externen Inline-Code:
 * - Aktiv auf kleinen Screens (Bootstrap md): max-width: 767.98px
 * - Bei Resize auf >= md: Instanzen zerstören und DOM zurücksetzen
 *
 * Anpassbar über:
 *   <html data-mftt-breakpoint="(max-width: 767.98px)">
 *   <html data-mftt-selector=".info-col">
 */
type MediaLike = MediaQueryList | MediaQueryListEvent;

let activeToggles: mfTextToggle[] = [];

function enableToggles(): void {
  if (activeToggles.length > 0) return; // schon aktiv
  activeToggles = mfTextToggle.initAll(getSelector(), {
    textSelector: ".project-info-text",
    collapsedLines: 4,
    // Icons werden automatisch per Template/Data-Attr gefunden,
    // du musst im HTML nur die Templates bereitstellen (siehe oben).
    showOnlyIfClamped: true,
  });
}

function disableToggles(): void {
  activeToggles.forEach((inst) => inst.destroy());
  activeToggles = [];
}

function handleMediaChange(e: MediaLike): void {
  const matches = "matches" in e ? e.matches : (e as MediaQueryList).matches;
  if (matches) {
    enableToggles();
  } else {
    disableToggles();
  }
}

function getBreakpoint(): string {
  const el = document.documentElement;
  return el.dataset.mfttBreakpoint || "(max-width: 991.98px)";
}

function getSelector(): string {
  const el = document.documentElement;
  return el.dataset.mfttSelector || ".info-col";
}

document.addEventListener("DOMContentLoaded", (): void => {
  const mq = window.matchMedia(getBreakpoint());
  handleMediaChange(mq);
  mq.addEventListener("change", handleMediaChange);
});
